import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { CalendarClock } from "lucide-react";
import { getScheduleChanges, getAlternatePlans } from "../lib/api";

const fmtDate = (iso) => {
  if (!iso) return "";
  const d = new Date(String(iso).slice(0, 10) + "T00:00:00");
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
};
const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const in7 = () => {
  const d = new Date(); d.setDate(d.getDate() + 7);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

/** Dashboard card: schedule changes taking effect or alternate plans ending in
 * the next 7 days. Hidden when there is nothing in the window. */
export default function ScheduleChangesWeekCard() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);

  useEffect(() => {
    const today = todayStr();
    const next7 = in7();
    Promise.all([
      getScheduleChanges({ status: "upcoming" }).catch(() => ({ data: [] })),
      getAlternatePlans({ active_only: true }).catch(() => ({ data: [] })),
    ]).then(([up, plans]) => {
      const starting = (up.data || [])
        .filter((c) => c.effective_date && c.effective_date >= today && c.effective_date <= next7)
        .map((c) => ({ key: `c-${c.id}`, name: c.student_name || "A student", label: "Takes effect", date: c.effective_date }));
      const ending = (plans.data || [])
        .filter((p) => p.end_date && p.end_date >= today && p.end_date <= next7)
        .map((p) => ({ key: `p-${p.id}`, name: p.student_name || "A student", label: `${p.destination || "Plan"} ends`, date: p.end_date }));
      setItems([...starting, ...ending].sort((a, b) => a.date.localeCompare(b.date)));
    });
  }, []);

  if (!items.length) return null;

  return (
    <Card className="animate-fade-in border-indigo-200" data-testid="schedule-changes-week-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2 text-[#00305E]">
          <CalendarClock className="w-4 h-4" />Schedule changes this week
        </CardTitle>
        <CardDescription>Taking effect or ending in the next 7 days</CardDescription>
      </CardHeader>
      <CardContent className="space-y-1.5">
        {items.map((it, i) => (
          <button
            key={it.key}
            onClick={() => navigate("/schedule-changes")}
            className="w-full flex items-center justify-between gap-2 text-left rounded-md px-2 py-1.5 hover:bg-slate-50"
            data-testid={`schedule-week-item-${i}`}
          >
            <span className="text-sm text-slate-800 truncate">
              <span className="font-medium">{it.name}</span>
              <span className="text-slate-400"> · </span>{it.label}
            </span>
            <span className="text-xs font-medium px-2 py-0.5 rounded bg-indigo-100 text-indigo-700 shrink-0">{fmtDate(it.date)}</span>
          </button>
        ))}
      </CardContent>
    </Card>
  );
}
